import React, { useEffect, useRef, useState } from 'react';
import './HomeScreen.css';

const HomeScreen: React.FC = () => {
  const [submitText, setSubmitText] = useState('Send Message \u2192');
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const revealRefs = useRef<HTMLDivElement[]>([]);

  useEffect(() => {
    // Load Google Fonts
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Permanent+Marker&family=DM+Serif+Display:ital@0;1&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    // Scroll reveal observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    revealRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
      document.head.removeChild(link);
    };
  }, []);

  const addRevealRef = (el: HTMLDivElement | null) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitDisabled(true);
    setSubmitText('Sending...');

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    try {
      const res = await fetch('/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.get('name'),
          business: formData.get('business'),
          email: formData.get('email'),
          phone: formData.get('phone'),
          type: formData.get('type'),
          message: formData.get('message'),
        }),
      });

      if (res.ok) {
        setSubmitText('Message Sent \u2713');
        form.reset();
      } else {
        setSubmitText('Something went wrong. Try again.');
      }
    } catch {
      setSubmitText('Something went wrong. Try again.');
    }

    setTimeout(() => {
      setSubmitText('Send Message \u2192');
      setSubmitDisabled(false);
    }, 3000);
  };

  const scrollTo = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const target = document.getElementById(id);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <a href="#" className="landing-logo">
          <img src="/static/images/logo.png" alt="Paiper" className="landing-logo-img" />
          Paiper<span>.</span>
        </a>
        <ul className="landing-nav-links">
          <li><a href="#services" onClick={scrollTo('services')}>Services</a></li>
          <li><a href="#pricing" onClick={scrollTo('pricing')}>Pricing</a></li>
          <li><a href="#how-it-works" onClick={scrollTo('how-it-works')}>How It Works</a></li>
          <li><a href="#examples" onClick={scrollTo('examples')}>Examples</a></li>
          <li><a href="#about" onClick={scrollTo('about')}>About</a></li>
          <li><a href="#contact" onClick={scrollTo('contact')} className="landing-nav-cta">Get in Touch</a></li>
        </ul>
      </nav>

      {/* Hero */}
      <section className="landing-hero">
        <div className="landing-hero-content">
          <div className="landing-hero-badge">Based in North Vernon, Indiana</div>
          <h1>You own your business. <em>Own your software too.</em></h1>
          <p>Custom software used to cost six figures and take months.<br /><em>New technology has changed that.</em><br /><br />I'm Humberto — a developer in Southern Indiana building real platforms for local businesses <a href="#pricing" onClick={scrollTo('pricing')} style={{ textDecoration: 'underline', fontWeight: 700, color: 'inherit' }}>at prices that actually make sense.</a></p>
          <p className="landing-hero-own-it">Your software. Your rules. Built for you.</p>
          <div className="landing-hero-actions">
            <a href="#contact" onClick={scrollTo('contact')} className="landing-btn-primary">Start a Conversation &rarr;</a>
            <a href="tel:8129009232" className="landing-btn-secondary">Call (812) 900-9232</a>
          </div>
        </div>
        <a href="https://www.amazon.com/dp/B0GVGFLW4S" target="_blank" rel="noopener noreferrer" className="landing-hero-book-link">
          <div className="landing-book-scene">
            <div className="landing-book-3d">
              {/* Front Cover */}
              <div className="landing-book-face landing-book-front">
                <svg xmlns="http://www.w3.org/2000/svg" width="250" height="375" viewBox="0 0 250 375">
                  <rect width="250" height="375" fill="#14100c"/>
                  <defs>
                    <pattern id="iso" width="44" height="50.8" patternUnits="userSpaceOnUse">
                      <path d="M 22 0 L 44 12.7 L 22 25.4 L 0 12.7 Z" fill="none" stroke="rgba(192,130,46,0.16)" strokeWidth="0.6"/>
                      <path d="M 0 12.7 L 22 25.4 L 22 50.8 L 0 38.1 Z" fill="none" stroke="rgba(192,130,46,0.1)" strokeWidth="0.6"/>
                      <path d="M 44 12.7 L 22 25.4 L 22 50.8 L 44 38.1 Z" fill="none" stroke="rgba(192,130,46,0.22)" strokeWidth="0.6"/>
                    </pattern>
                    <linearGradient id="bar" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#c0822e"/>
                      <stop offset="100%" stopColor="rgba(192,130,46,0)"/>
                    </linearGradient>
                  </defs>
                  <rect width="250" height="375" fill="url(#iso)"/>
                  <rect x="0" y="0" width="3" height="375" fill="url(#bar)"/>
                  <text x="26" y="80" fontFamily="Georgia,serif" fontSize="30" fontWeight="700" fill="#f0e8da" letterSpacing="-0.5">Software</text>
                  <text x="26" y="114" fontFamily="Georgia,serif" fontSize="30" fontWeight="700" fill="#f0e8da" letterSpacing="-0.5">as a</text>
                  <text x="26" y="148" fontFamily="Georgia,serif" fontSize="30" fontWeight="700" fill="#c0822e" fontStyle="italic" letterSpacing="-0.5">Material</text>
                  <rect x="26" y="162" width="24" height="1.5" fill="#c0822e" opacity="0.5"/>
                  <text x="26" y="184" fontFamily="Helvetica,Arial,sans-serif" fontSize="10" fill="#8a7a60">How AI Creates a New Trade</text>
                  <text x="26" y="197" fontFamily="Helvetica,Arial,sans-serif" fontSize="10" fill="#8a7a60">and the Towns It Could Rebuild</text>
                  <line x1="26" y1="240" x2="224" y2="240" stroke="rgba(192,130,46,0.12)" strokeWidth="0.5"/>
                  <text x="26" y="258" fontFamily="monospace" fontSize="8.5"><tspan fill="#daa050">listen</tspan><tspan fill="#8a7060">(</tspan><tspan fill="#ba9040">town</tspan><tspan fill="#8a7060">.</tspan><tspan fill="#caa060">needs</tspan><tspan fill="#8a7060">)</tspan></text>
                  <text x="26" y="272" fontFamily="monospace" fontSize="8.5"><tspan fill="#daa050">build</tspan><tspan fill="#8a7060">(</tspan><tspan fill="#ba9040">local</tspan><tspan fill="#8a7060">.</tspan><tspan fill="#caa060">tools</tspan><tspan fill="#8a7060">)</tspan></text>
                  <text x="26" y="286" fontFamily="monospace" fontSize="8.5"><tspan fill="rgba(192,130,46,0.6)">return</tspan><tspan fill="#8a7060"> </tspan><tspan fill="#ba9040">ownership</tspan></text>
                  <text x="26" y="350" fontFamily="Helvetica,Arial,sans-serif" fontSize="9" fill="#6a5a40" letterSpacing="1.5">HUMBERTO DE LA CRUZ SANTOS</text>
                </svg>
              </div>
              {/* Spine */}
              <div className="landing-book-face landing-book-spine">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="375" viewBox="0 0 24 375">
                  <rect width="24" height="375" fill="#1a150f"/>
                  <rect x="0" y="0" width="2" height="375" fill="rgba(192,130,46,0.15)"/>
                  <rect x="22" y="0" width="2" height="375" fill="rgba(192,130,46,0.08)"/>
                  <text transform="rotate(90) translate(40, -7)" fontFamily="Georgia,serif" fontSize="10" fontWeight="700" fill="#c0822e" letterSpacing="0.5">Software as a Material</text>
                  <text transform="rotate(90) translate(260, -7)" fontFamily="Helvetica,Arial,sans-serif" fontSize="7" fill="#6a5a40" letterSpacing="1">DE LA CRUZ SANTOS</text>
                </svg>
              </div>
              {/* Page edges */}
              <div className="landing-book-face landing-book-top"></div>
              <div className="landing-book-face landing-book-bottom"></div>
              <div className="landing-book-face landing-book-pages"></div>
            </div>
          </div>
          <span className="landing-book-cta">Read the book &rarr;</span>
        </a>
      </section>

      {/* Trusted */}
      <div className="landing-trusted">
        <p>Serving businesses across Southern Indiana</p>
        <div className="landing-trusted-logos">
          <span className="landing-trusted-logo">Columbus</span>
          <span className="landing-trusted-logo">&middot;</span>
          <span className="landing-trusted-logo">North Vernon</span>
          <span className="landing-trusted-logo">&middot;</span>
          <span className="landing-trusted-logo">Seymour</span>
          <span className="landing-trusted-logo">&middot;</span>
          <span className="landing-trusted-logo">Shelbyville</span>
          <span className="landing-trusted-logo">&middot;</span>
          <span className="landing-trusted-logo">Bloomington</span>
        </div>
      </div>

      {/* Founding Client */}
      <div className="landing-founding-banner landing-reveal" ref={addRevealRef}>
        <p>First project? <a href="#contact" onClick={scrollTo('contact')}>Ask about founding client pricing.</a></p>
      </div>

      {/* Services */}
      <section className="landing-services" id="services">
        <div className="landing-section-header landing-reveal" ref={addRevealRef}>
          <div className="landing-section-label">What You Get</div>
          <h2>Software built around your business, not the other way around</h2>
          <p>Every business runs differently. I build one system that fits the way you actually work — your workflows, your team, your customers. You own everything I build. No licensing, no lock-in.</p>
        </div>
        <div className="landing-services-grid">
          <div className="landing-service-card landing-reveal" ref={addRevealRef}>
            <div className="landing-service-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8" /><path d="M12 17v4" />
              </svg>
            </div>
            <h3>Web Applications</h3>
            <p>Customer portals, dashboards, booking systems, internal tools — anything your team or customers need to access from a browser.</p>
          </div>
          <div className="landing-service-card landing-reveal" ref={addRevealRef}>
            <div className="landing-service-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="2" width="14" height="20" rx="2" /><path d="M12 18h.01" />
              </svg>
            </div>
            <h3>Mobile Apps</h3>
            <p>Native iOS and Android apps for your customers or your team in the field. Built for speed and ease of use.</p>
          </div>
          <div className="landing-service-card landing-reveal" ref={addRevealRef}>
            <div className="landing-service-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 6V2H8" /><path d="m8 18-4 4V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2Z" /><path d="M2 12h2" /><path d="M9 11v2" /><path d="M15 11v2" /><path d="M20 12h2" />
              </svg>
            </div>
            <h3>Automation</h3>
            <p>Replace the manual busywork. Invoicing, scheduling, notifications, reports — if you do it repeatedly, I can automate it.</p>
          </div>
          <div className="landing-service-card landing-reveal" ref={addRevealRef}>
            <div className="landing-service-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 20a6 6 0 0 0-12 0" /><circle cx="12" cy="10" r="4" /><circle cx="12" cy="12" r="10" />
              </svg>
            </div>
            <h3>Ongoing Support</h3>
            <p>I'm not a faceless agency. When something breaks or you need a change, I'm a phone call away — right down the road.</p>
          </div>
          <div className="landing-service-card landing-reveal" ref={addRevealRef}>
            <div className="landing-service-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="m8 6 4-4 4 4" /><path d="M12 2v10.3" /><path d="M4 13.4A2 2 0 0 0 3 15v1a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-1a2 2 0 0 0-1-1.7" /><path d="M8 18l4 4 4-4" /><path d="M12 22v-10.3" />
              </svg>
            </div>
            <h3>System Integration</h3>
            <p>Connect your existing tools — QuickBooks, Square, Google Workspace, whatever you already use — into one seamless workflow.</p>
          </div>
          <div className="landing-service-card landing-reveal" ref={addRevealRef}>
            <div className="landing-service-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" />
              </svg>
            </div>
            <h3>Data &amp; Insights</h3>
            <p>See how your business is actually doing. Custom dashboards that show the numbers that matter to you, in real time.</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works">
        <div className="landing-section-header landing-reveal" ref={addRevealRef}>
          <div className="landing-section-label">How It Works</div>
          <h2>Simple, honest process</h2>
          <p>No jargon. No surprises. Just a straightforward path from idea to working software.</p>
        </div>
        <div className="landing-how-it-works">
          <div className="landing-steps">
            <div className="landing-step landing-reveal" ref={addRevealRef}>
              <div className="landing-step-number-col">
                <div className="landing-step-number">1</div>
                <div className="landing-step-line"></div>
              </div>
              <div className="landing-step-content">
                <h3>We Talk</h3>
                <p>A free 15-minute conversation where I learn about your business, your pain points, and what a better day would look like. No pressure, no pitch.</p>
              </div>
            </div>
            <div className="landing-step landing-reveal" ref={addRevealRef}>
              <div className="landing-step-number-col">
                <div className="landing-step-number">2</div>
                <div className="landing-step-line"></div>
              </div>
              <div className="landing-step-content">
                <h3>I Build a Plan</h3>
                <p>I put together a clear proposal — what I'll build, how long it takes, and what it costs. Flat pricing, no hourly surprises.</p>
              </div>
            </div>
            <div className="landing-step landing-reveal" ref={addRevealRef}>
              <div className="landing-step-number-col">
                <div className="landing-step-number">3</div>
                <div className="landing-step-line"></div>
              </div>
              <div className="landing-step-content">
                <h3>You See It Take Shape</h3>
                <p>I build in short sprints and show you progress every week. You give feedback, I adjust. Nothing gets built in a black box.</p>
              </div>
            </div>
            <div className="landing-step landing-reveal" ref={addRevealRef}>
              <div className="landing-step-number-col">
                <div className="landing-step-number">4</div>
                <div className="landing-step-line"></div>
              </div>
              <div className="landing-step-content">
                <h3>It Goes Live</h3>
                <p>Your software launches and I stick around. Updates, fixes, new ideas — I'm your tech person for the long haul.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="landing-pricing" id="pricing">
        <div className="landing-section-header landing-reveal" ref={addRevealRef}>
          <div className="landing-section-label">Pricing</div>
          <h2>Simple, honest pricing</h2>
          <p>No hourly surprises. No hidden fees. You know exactly what you're paying from day one.</p>
        </div>
        <div className="landing-pricing-grid">
          <div className="landing-pricing-card landing-reveal" ref={addRevealRef}>
            <div className="landing-pricing-tier">One-Time Build</div>
            <div className="landing-pricing-amount">$2,000 &ndash; $4,000</div>
            <div className="landing-pricing-period">most projects</div>
            <div className="landing-pricing-features">
              <div className="landing-pricing-feature"><span className="landing-pricing-check">{"\u2713"}</span> Custom-built platform for your business</div>
              <div className="landing-pricing-feature"><span className="landing-pricing-check">{"\u2713"}</span> Scoped to exactly what you need</div>
              <div className="landing-pricing-feature"><span className="landing-pricing-check">{"\u2713"}</span> Weekly progress updates during build</div>
              <div className="landing-pricing-feature"><span className="landing-pricing-check">{"\u2713"}</span> Training and handoff when it's ready</div>
            </div>
            <p className="landing-pricing-payment-note">Flexible payments available — split across two or three months.</p>
          </div>
          <div className="landing-pricing-card landing-pricing-popular landing-reveal" ref={addRevealRef}>
            <div className="landing-pricing-badge">Optional</div>
            <div className="landing-pricing-tier">Monthly</div>
            <div className="landing-pricing-amount">$250</div>
            <div className="landing-pricing-period">per month</div>
            <div className="landing-pricing-features">
              <div className="landing-pricing-feature"><span className="landing-pricing-check">{"\u2713"}</span> Hosting &amp; infrastructure</div>
              <div className="landing-pricing-feature"><span className="landing-pricing-check">{"\u2713"}</span> Technical support</div>
              <div className="landing-pricing-feature"><span className="landing-pricing-check">{"\u2713"}</span> <strong>Ongoing improvements &amp; updates</strong></div>
              <div className="landing-pricing-feature"><span className="landing-pricing-check">{"\u2713"}</span> Direct access to me — no tickets</div>
            </div>
            <p className="landing-pricing-payment-note">Cancel anytime — no contracts.</p>
          </div>
        </div>
        <p className="landing-pricing-note landing-reveal" ref={addRevealRef}>Need something bigger? Larger projects get quoted separately — always flat-rate, always agreed on before I start.</p>
      </section>

      {/* Examples */}
      <section className="landing-examples" id="examples">
        <div className="landing-section-header landing-reveal" ref={addRevealRef}>
          <div className="landing-section-label">What's Now Possible</div>
          <h2>This used to be out of reach. Not anymore.</h2>
          <p>These are the kinds of platforms that only big companies could build. The technology has caught up. Now your business can have them too.</p>
        </div>
        <div className="landing-examples-grid">
          <div className="landing-example-card landing-reveal" ref={addRevealRef}>
            <div className="landing-example-type">Contractor</div>
            <h3>Job Tracking App</h3>
            <p>Your crew texts you photos from the job site. What if they logged everything in an app you owned, and your customers could see progress in real time? No more chasing updates or digging through group chats.</p>
          </div>
          <div className="landing-example-card landing-reveal" ref={addRevealRef}>
            <div className="landing-example-type">Restaurant</div>
            <h3>Custom Ordering System</h3>
            <p>You're paying a third party a cut of every order to use their system. What if you had your own — built around your menu, your kitchen flow, your delivery zones? Every dollar stays with you.</p>
          </div>
          <div className="landing-example-card landing-reveal" ref={addRevealRef}>
            <div className="landing-example-type">Service Business</div>
            <h3>Booking &amp; Invoicing Platform</h3>
            <p>Right now you're juggling a calendar app, an invoicing tool, and your email. What if customers booked online, got reminders automatically, and invoices went out the moment the job was done?</p>
          </div>
          <div className="landing-example-card landing-reveal" ref={addRevealRef}>
            <div className="landing-example-type">Manufacturer</div>
            <h3>Shop Floor Dashboard</h3>
            <p>You track jobs on whiteboards and spreadsheets. What if your team had a live dashboard showing every order, every stage of production, and your customers could check their status without calling?</p>
          </div>
          <div className="landing-example-card landing-reveal" ref={addRevealRef}>
            <div className="landing-example-type">Retail</div>
            <h3>Inventory &amp; POS Integration</h3>
            <p>Your online store and your shop counter don't talk to each other. What if inventory updated in real time across both, and you got alerts before you ran low on your best sellers?</p>
          </div>
          <div className="landing-example-card landing-reveal" ref={addRevealRef}>
            <div className="landing-example-type">Farm / Agriculture</div>
            <h3>Sales &amp; CSA Manager</h3>
            <p>You manage CSA subscriptions in a spreadsheet and take farmers market orders by hand. What if one app handled it all — built for someone who works with their hands, not a keyboard?</p>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about">
        <div className="landing-about landing-reveal" ref={addRevealRef}>
          <div className="landing-section-header">
            <div className="landing-section-label">Who's Behind This</div>
            <h2>A real person, not an agency</h2>
          </div>
          <div className="landing-about-content">
            <img src="/static/images/profile.jpeg" alt="Humberto" className="landing-about-photo" />
            <div className="landing-about-text">
              <h3>Humberto</h3>
              <p>I'm a software developer living on a homestead in North Vernon, Indiana. I've been building apps since 2013 — five of them are live on the App Store right now. I work with web, iOS, and Android, and I care about building things that actually work for the people using them.</p>
              <p>New tools have changed what one developer can build. Work that used to take a team of five now takes me weeks. That's why I can offer custom software at prices that would've been impossible two years ago.</p>
              <p>I'm not a big agency. I'm the person who builds your software, answers your call, and fixes the thing that broke on a Saturday. That's the kind of relationship I want with every client.</p>
              <div className="landing-about-stats">
                <div className="landing-stat">
                  <div className="landing-stat-number">10+</div>
                  <div className="landing-stat-label">Years Building</div>
                </div>
                <div className="landing-stat">
                  <div className="landing-stat-number">5</div>
                  <div className="landing-stat-label">Live Apps</div>
                </div>
                <div className="landing-stat">
                  <div className="landing-stat-number">3</div>
                  <div className="landing-stat-label">Platforms</div>
                </div>
              </div>
              <div className="landing-app-showcase">
                <p className="landing-app-showcase-label">Live on the App Store</p>
                <div className="landing-app-grid">
                  <a href="https://apps.apple.com/us/app/notas-ai-chat-capture/id6755617260" target="_blank" rel="noopener noreferrer" className="landing-app-item">
                    <img src="/static/images/notas-icon.png" alt="Notas AI" />
                    <span>Notas AI</span>
                  </a>
                  <a href="https://apps.apple.com/us/app/paiper/id6475222980" target="_blank" rel="noopener noreferrer" className="landing-app-item">
                    <img src="/static/images/paiper-icon.png" alt="Paiper" />
                    <span>Paiper</span>
                  </a>
                  <a href="https://apps.apple.com/us/app/fieldslip/id6757338082" target="_blank" rel="noopener noreferrer" className="landing-app-item">
                    <img src="/static/images/fieldslip-icon.png" alt="FieldSlip" />
                    <span>FieldSlip</span>
                  </a>
                  <a href="https://apps.apple.com/us/app/crear-app/id1591314959" target="_blank" rel="noopener noreferrer" className="landing-app-item">
                    <img src="/static/images/crear-icon.png" alt="Crear App" />
                    <span>Crear</span>
                  </a>
                  <a href="https://apps.apple.com/us/app/draftsmith-ai-writer/id6742753119" target="_blank" rel="noopener noreferrer" className="landing-app-item">
                    <img src="/static/images/draftsmith-icon.png" alt="Draftsmith" />
                    <span>Draftsmith</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="landing-contact" id="contact">
        <div className="landing-contact-wrapper">
          <div className="landing-section-header landing-reveal" ref={addRevealRef}>
            <div className="landing-section-label">Let's Talk</div>
            <h2>Tell me about your business</h2>
            <p>No commitment, no pressure. Just a quick conversation about what's slowing you down and whether I can help.</p>
            <div className="landing-contact-direct">
              <a href="tel:8129009232" className="landing-contact-phone">(812) 900-9232</a>
              <span>or fill out the form below</span>
            </div>
          </div>
          <form className="landing-reveal" ref={addRevealRef as any} onSubmit={handleSubmit}>
            <div className="landing-form-row">
              <div className="landing-form-group">
                <label htmlFor="name">Your Name</label>
                <input type="text" id="name" name="name" placeholder="John Smith" required />
              </div>
              <div className="landing-form-group">
                <label htmlFor="business">Business Name</label>
                <input type="text" id="business" name="business" placeholder="Smith's Plumbing" />
              </div>
            </div>
            <div className="landing-form-row">
              <div className="landing-form-group">
                <label htmlFor="email">Email</label>
                <input type="email" id="email" name="email" placeholder="john@example.com" required />
              </div>
              <div className="landing-form-group">
                <label htmlFor="phone">Phone <span style={{ color: 'var(--warm-gray)', fontWeight: 400 }}>(optional)</span></label>
                <input type="tel" id="phone" name="phone" placeholder="(812) 555-0123" />
              </div>
            </div>
            <div className="landing-form-group">
              <label htmlFor="type">What kind of business do you run?</label>
              <select id="type" name="type" defaultValue="">
                <option value="" disabled>Select one...</option>
                <option>Contractor / Trades</option>
                <option>Restaurant / Food Service</option>
                <option>Retail / Shop</option>
                <option>Service Business</option>
                <option>Manufacturing</option>
                <option>Farm / Agriculture</option>
                <option>Healthcare / Wellness</option>
                <option>Other</option>
              </select>
            </div>
            <div className="landing-form-group">
              <label htmlFor="message">What's on your mind?</label>
              <textarea id="message" name="message" placeholder="Tell me what's frustrating you about your current tools, or an idea you've been thinking about..."></textarea>
            </div>
            <button type="submit" className="landing-form-submit" disabled={submitDisabled} style={submitDisabled ? { background: 'var(--sage)' } : {}}>
              {submitText}
            </button>
            <p className="landing-form-note">I'll get back to you within 24 hours. Usually much sooner.</p>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-logo">Paiper<span>.</span></div>
        <p>Custom software for small businesses &middot; North Vernon, Indiana</p>
        <p style={{ marginTop: '0.5rem' }}><a href="tel:8129009232">(812) 900-9232</a> &middot; <a href="mailto:humberto@paiperapps.com">humberto@paiperapps.com</a></p>
        <p style={{ marginTop: '1.5rem', fontSize: '0.75rem' }}>&copy; 2026 Paiper. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default HomeScreen;
